import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEOHead from '../components/SEOHead';
import QuoteLeadForm from '../components/QuoteLeadForm';
import { SITE_PHONE_DISPLAY, SITE_PHONE_TEL } from '../constants/site';

const STEPS = [
  {
    title: 'Apply',
    text: 'Submit the 5-field quote request with your asset and business details.',
  },
  {
    title: 'Match',
    text: 'We map your file to lenders based on policy fit, structure, and timing.',
  },
  {
    title: 'Approve',
    text: 'Receive approval pathways and structure options for your scenario.',
  },
  {
    title: 'Fund',
    text: 'Settle quickly so your asset is deployed and earning sooner.',
  },
];

export default function HowItWorksPage() {
  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="how-it-works-page">
      <SEOHead
        title="How It Works | Flash Funding"
        description="See how Flash Funding guides your application from quote request to lender match, approval, and funding."
        canonical="/how-it-works"
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10" data-testid="how-it-works-hero-section">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-4">How it works</h1>
        <p className="text-white/70 text-sm sm:text-base mb-6">A practical four-step process built for Australian operators and SMB decision-makers.</p>
        <div className="flex flex-wrap gap-3" data-testid="how-it-works-hero-ctas">
          <a href="#how-it-works-quote-form" data-testid="how-it-works-primary-link">
            <Button className="rounded-full bg-purple-600 hover:bg-purple-500 text-white text-xs uppercase tracking-[0.14em] font-bold px-7 py-6" data-testid="how-it-works-primary-cta">
              Get my rate in 60 seconds
              <ArrowRight size={14} className="ml-2" />
            </Button>
          </a>
          <a href={SITE_PHONE_TEL} data-testid="how-it-works-call-link">
            <Button variant="outline" className="rounded-full border-purple-400/40 bg-navy-800 text-white hover:bg-purple-500/15 text-xs uppercase tracking-[0.14em] font-bold px-7 py-6" data-testid="how-it-works-secondary-cta">
              Call {SITE_PHONE_DISPLAY}
            </Button>
          </a>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10" data-testid="how-it-works-steps-section">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {STEPS.map((step, index) => (
            <article key={step.title} className="rounded-2xl border border-purple-500/20 bg-navy-800/70 p-5" data-testid={`how-it-works-step-card-${index + 1}`}>
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-purple-500/20 text-purple-200 text-sm mb-3" data-testid={`how-it-works-step-number-${index + 1}`}>{index + 1}</span>
              <h2 className="text-white font-semibold mb-2" data-testid={`how-it-works-step-title-${index + 1}`}>{step.title}</h2>
              <p className="text-white/70 text-sm" data-testid={`how-it-works-step-text-${index + 1}`}>{step.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="how-it-works-quote-form" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="how-it-works-quote-form-section">
        <QuoteLeadForm sourcePage="/how-it-works" dataTestIdPrefix="how-it-works-quote-form" />
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10" data-testid="how-it-works-links-section">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: 'Truck & Trailer Finance', path: '/truck-and-trailer-finance' },
            { label: 'Equipment Finance', path: '/equipment-finance' },
            { label: 'Vehicle Finance', path: '/vehicle-finance' },
            { label: 'Business Loans', path: '/business-loans' },
          ].map((item) => (
            <Link key={item.path} to={item.path} className="rounded-xl border border-white/10 bg-navy-800/70 p-4 text-sm text-purple-300 hover:text-purple-200" data-testid={`how-it-works-link-${item.path.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
              {item.label}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
